import { notification } from "antd";
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import ProfileDetails from "../components/Profile/ProfileDetails";
import Recommendations from "../components/Profile/Recommendations";
import { apiServices } from "../services/Api";
import leftarrow from "../assets/images/u_angle-left-b.svg";
import ellipseSVG from "../assets/images/ellipse.svg";
import SummarySlider from "../components/SummarySlider";
import { safeJSONParse } from "../utils/jsonHelper";
import { getStatusDisplayText } from "../utils/statusUtils";
// import KiteConnect from "kiteconnect";

const ProfileSummary = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [toggleNotifyBtn, setToggleNotifyBtn] = useState(false);
  const [summaryData, setSummaryData] = useState({});
  const [advisor, setAdvisor] = useState({});
  const { id } = useParams();

  const [riskRewardRatio, setRiskRewardRatio] = useState("-");
  const [epl, setEpl] = useState(0);
  const [sl, setSl] = useState(0);
  const [tp, setTp] = useState(0);

  const getSummaryData = useCallback(async () => {
    if (!id) {
      notification.error({
        message: "Error",
        description: "Invalid call ID. Please check the URL and try again.",
      });
      return;
    }

    if (location.state) {
      setSummaryData(location.state);
      setEpl(location.state?.entryPriceLower);
      setTp(location.state?.targetPrice);
      setSl(location.state?.stoploss);
      return;
    }

    try {
      const response = await apiServices.get(`/calls/${id}`);
      setSummaryData(response);
      setEpl(response?.entryPriceLower);
      setTp(response?.targetPrice);
      setSl(response?.stoploss);
    } catch (e) {
      console.log("error:", e);
      notification.error({
        message: "Error",
        description: "Failed to load call details. Please try again.",
      });
    }
  }, [id, location.state]);

  const [advisorRecommendations, setAdvisorRecommendations] = useState([]);
  const [advisorComments, setAdvisorComments] = useState([]);

  const parseRecommendationString = (value) => {
    const parsed = safeJSONParse(value, null);
    if (Array.isArray(parsed) && parsed.length) return parsed;
    if (typeof value === "string" && value.trim() !== "") {
      return value
        .split("|")
        .map((text, idx) => ({ id: idx, text: text.trim() }))
        .filter((item) => item.text !== "");
    }
    return [];
  };

  useEffect(() => {
    if (summaryData) {
      setAdvisorRecommendations(
        parseRecommendationString(summaryData?.recommendations)
      );
      setAdvisorComments(parseRecommendationString(summaryData?.comments));
    }
  }, [summaryData]);

  // Get Advisor Data
  const getAdvisor = async (advId) => {
    try {
      const advisorsData = await apiServices.get(`advisors/${advId}`);
      setAdvisor(advisorsData);
      return advisorsData;
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (!id) return;
    const savedStatus = localStorage.getItem(`notify_${id}`);
    if (savedStatus !== null) {
      const status = savedStatus === "true";
      setToggleNotifyBtn(status);
    }
  }, [id]);

  const checkSubscriptionStatus = useCallback(async () => {
    if (!id) return;
    try {
      const subscribedCalls = await apiServices.get("/users/calls/notify");
      if (!Array.isArray(subscribedCalls)) return;

      const alreadySubscribed = subscribedCalls.some((call) => {
        const checkMatch = (targetId) => {
          if (!targetId) return false;
          const sTarget = String(targetId).toLowerCase();
          const sId = String(id).toLowerCase();
          const sSumId = summaryData?.id
            ? String(summaryData.id).toLowerCase()
            : null;
          const sSumCallId = summaryData?.callId
            ? String(summaryData.callId).toLowerCase()
            : null;

          return (
            sTarget === sId ||
            (sSumId && sTarget === sSumId) ||
            (sSumCallId && sTarget === sSumCallId)
          );
        };
        return checkMatch(call.id) || checkMatch(call.callId);
      });

      setToggleNotifyBtn(alreadySubscribed);
      localStorage.setItem(`notify_${id}`, alreadySubscribed.toString());
    } catch (error) {
      console.log("Error checking subscription status:", error);
    }
  }, [id, summaryData?.id, summaryData?.callId]);

  const handleNotify = async () => {
    if (!id) return;
    const previousState = toggleNotifyBtn;
    try {
      setToggleNotifyBtn(!previousState);

      let response;
      if (previousState) {
        response = await apiServices.delete(`/users/calls/notify`, {
          callId: id,
        });
      } else {
        response = await apiServices.post(`/users/calls/notify`, {
          callId: id,
        });
      }

      let isNowSubscribed;
      if (response?.subscribed !== undefined) {
        isNowSubscribed = response.subscribed;
      } else {
        isNowSubscribed = !previousState;
      }

      setToggleNotifyBtn(isNowSubscribed);
      localStorage.setItem(`notify_${id}`, isNowSubscribed.toString());

      notification.success({
        message: "Success",
        description: isNowSubscribed
          ? "You have been subscribed to this call."
          : "You have been unsubscribed from this call.",
      });
    } catch (error) {
      setToggleNotifyBtn(previousState);
      notification.error({
        message: "Error",
        description: `Failed to ${
          previousState ? "unfollow" : "subscribe"
        }. Please try again.`,
      });
    }
  };

  useEffect(() => {
    if (id) {
      getSummaryData();
      checkSubscriptionStatus();
      const refreshInterval = setInterval(() => {
        getSummaryData();
        checkSubscriptionStatus();
      }, 300000);
      return () => clearInterval(refreshInterval);
    } else {
      notification.error({
        message: "Invalid URL",
        description: "Missing ID. Redirecting...",
      });
      setTimeout(() => navigate("/dashboard"), 3000);
    }
  }, [id, getSummaryData, checkSubscriptionStatus, navigate]);

  useEffect(() => {
    if (summaryData?.advisorId) {
      getAdvisor(summaryData?.advisorId);
    }
  }, [summaryData?.advisorId]);

  const getStatusWithColor = (status, percentToTrigger) => {
    const statusText = getStatusDisplayText(status, percentToTrigger);
    if (
      statusText.includes("LIVE (Inside") ||
      statusText.includes("CLOSED (Target hit)")
    ) {
      return { text: statusText, color: "#10B981" };
    } else if (statusText.includes("CLOSED (SL hit)")) {
      return { text: statusText, color: "#EF4444" };
    } else if (statusText.includes("LIVE (Outside")) {
      return { text: statusText, color: "#F59E0B" };
    } else {
      return { text: statusText, color: "#1569AB" };
    }
  };

  useEffect(() => {
    if (epl && sl && tp) {
      const potentialLoss = Math.abs(epl - sl);
      const potentialGain = Math.abs(tp - epl);

      if (potentialLoss > 0 && potentialGain > 0) {
        const ratioValue = potentialGain / potentialLoss;
        setRiskRewardRatio(`1 : ${ratioValue.toFixed(2)}`);
      }
    }
  }, [epl, tp, sl]);

  const importantInformation = [
    {
      id: 1,
      text: "Always login to your broker app at 9:15am if you have any active positions overnight. \n  - Why so? \n    Often stocks open with a % price difference above / below previous day's closing price. It may lead to your Target / SL orders getting skipped on your broker app. Exit your trade manually in case that happens.",
    },
    {
      id: 2,
      text: "Entry Price Range: It is advised to not enter a trade if Current Price has moved outside the Entry Price Range, as the Risk:Reward is no longer favorable.\n> Duration: The duration mentioned is tentative. The trade may achieve its Target or SL earlier. Place a SL GTT order mandatorily to limit your losses.",
    },
    {
      id: 3,
      text: "Quantity & Risk Management:\n> It is advised to trade Options & Futures with only 1 lot quantity if you're a new trader. Else, ensure to manage your risk if trading with multiple lots.\n> The amount of money you could lose if SL hits for a trade is your Risk Per Trade. It's a good practice to keep Risk Per Trade <1% of your Account Value.\n> It's a good practice to keep Total Open Risk combined for all open trades <3-5% of Account Value, to ensure you don't wipe out your capital quickly.",
    },
    {
      id: 4,
      text: "Disclaimer:\nAlways conduct your own research and consult with your investment advisor before making any financial decisions.",
    },
  ];

  return (
    <div className="w-full py-4 px-3 sm:py-5 sm:px-6 lg:px-[3.75rem]">
      <div className="bg-white rounded-lg border border-brand-border p-4 lg:p-6 mb-4 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full md:w-auto">
          <button
            className="flex w-10 h-10 lg:w-[52px] lg:h-[50px] justify-center items-center rounded-lg border border-[#939393] bg-white hover:bg-gray-50 transition-colors flex-shrink-0"
            onClick={() => {
              const sourcePage = location.state?.from || "/dashboard";
              navigate(sourcePage);
            }}
          >
            <img src={leftarrow} alt="back" className="w-5 h-5 lg:w-6 lg:h-6" />
          </button>

          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 flex-grow">
            <div className="flex items-center gap-3">
              <span
                className={`py-0.5 px-2 text-[10px] lg:text-xs font-bold text-white rounded uppercase ${
                  summaryData?.transactionType === "SELL"
                    ? "bg-[#DE3748]"
                    : "bg-primary-green"
                }`}
              >
                {summaryData.transactionType || "BUY"}
              </span>
              <h1 className="text-[#454545] font-roboto text-xl lg:text-[24px] font-bold truncate">
                {summaryData.tradingSymbol || "-"}
              </h1>
            </div>

            <div className="hidden sm:flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-gray-400" />
              <span
                className="font-semibold text-sm lg:text-lg"
                style={{
                  color: getStatusWithColor(
                    summaryData?.status,
                    summaryData?.percentToTrigger
                  ).color,
                }}
              >
                {
                  getStatusWithColor(
                    summaryData?.status,
                    summaryData?.percentToTrigger
                  ).text
                }
              </span>
            </div>
          </div>
        </div>

        <div className="w-full md:w-auto flex flex-col sm:flex-row items-center justify-between gap-4 md:gap-0">
          <div className="flex sm:hidden w-full items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-2">
              <img src={ellipseSVG} className="w-1.5 h-1.5" alt="" />
              <span className="text-xs text-[#454545]">Status</span>
              <span
                className="text-xs font-bold truncate max-w-[150px]"
                style={{
                  color: getStatusWithColor(
                    summaryData?.status,
                    summaryData?.percentToTrigger
                  ).color,
                }}
              >
                {
                  getStatusWithColor(
                    summaryData?.status,
                    summaryData?.percentToTrigger
                  ).text
                }
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-[#454545]">Conf.</span>
              <span className="text-xs font-bold">
                {summaryData?.confidence || 0}%
              </span>
            </div>
          </div>

          <button
            className={`w-full md:w-auto py-2.5 px-6 rounded-lg text-sm lg:text-base font-normal transition-colors duration-200 border font-roboto flex justify-center items-center ${
              toggleNotifyBtn
                ? "bg-primary text-white border-transparent hover:bg-[#125a96]"
                : "bg-white text-[#454545] border-[#1569AB] hover:bg-gray-50"
            }`}
            onClick={handleNotify}
          >
            {toggleNotifyBtn ? "Unfollow" : "Notify me"}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg border-[0.6px] border-brand-border p-4 md:p-6 lg:p-8 flex flex-col gap-6">
        <div className="flex flex-col md:flex-row justify-between items-start gap-4 w-full">
          <div className="flex-grow">
            <ProfileDetails
              name={
                advisor?.firstName
                  ? `${advisor.firstName} ${advisor.lastName}`
                  : "Loading..."
              }
              dateCreate={summaryData.createdAt}
              dateUpdate={summaryData.updatedAt}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {summaryData.segmentTag && (
              <div className="text-[#1569AB] bg-[#E3F2FD] font-inter text-xs px-3 py-1.5 rounded-md font-medium">
                {summaryData.segmentTag}
              </div>
            )}
            {Array.isArray(summaryData.tags) &&
              summaryData.tags.map((tag, idx) => (
                <div
                  key={idx}
                  className="text-[#1569AB] bg-[#E3F2FD] font-inter text-xs px-3 py-1.5 rounded-md font-medium"
                >
                  {tag}
                </div>
              ))}
          </div>
        </div>

        <div className="h-[1px] bg-[#d9d9d9] w-full" />

        <div className="w-full pt-2">
          <SummarySlider
            minValue={summaryData.stoploss}
            maxValue={summaryData.targetPrice}
            startValue={summaryData.entryPriceLower}
            endValue={summaryData.entryPriceUpper}
            currentPrice={summaryData.price}
            status={summaryData.status}
            transactionType={summaryData.transactionType}
          />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex">
            <div className="py-1 px-3 bg-[#F0EFEF] border border-[#7F7F7F] text-[#454545] text-xs rounded-l-md border-r-0">
              Overnight (NRML)
            </div>
            <div className="py-1 px-3 border border-brand-border text-gray-400 text-xs rounded-r-md">
              Intraday (MIS)
            </div>
          </div>
          {/* <div className="hidden lg:block text-sm font-medium font-inter">
            Confidence: {summaryData?.confidence}%
          </div> */}
        </div>

        <div className="bg-white rounded-lg border border-brand-border p-4 md:p-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-y-6 gap-x-4">
            <StatItem
              label="Current Price"
              value={summaryData?.price}
              valueColor="#1569AB"
            />

            <StatItem label="Target" value={summaryData?.targetPrice} />

            <StatItem label="Stop Loss" value={summaryData.stoploss} />

            <StatItem
              label="Duration"
              value={`${summaryData.duration || 0} Days`}
            />

            <StatItem
              label="Entry price range"
              value={`${summaryData?.entryPriceLower || 0}-${
                summaryData?.entryPriceUpper || 0
              }`}
            />

            {/* <StatItem label="Capital required" value={summaryData?.capitalRequired} /> */}

            <StatItem
              label="Max. profit"
              value={`${Math.round(
                summaryData?.maximumProfitPercentage || 0
              )}%`}
            />

            <StatItem
              label="Max. Loss"
              value={`${Math.round(summaryData?.minimumLossPercentage || 0)}%`}
            />

            <StatItem label="Risk : Reward" value={riskRewardRatio} />
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-4 lg:gap-8 pb-4 w-full justify-start">
          <Recommendations
            heading="Advisor Recommendations"
            list={[...advisorComments, ...advisorRecommendations]}
          />
          <Recommendations
            heading="Important information"
            list={importantInformation}
          />
        </div>
      </div>
    </div>
  );
};

const StatItem = ({ label, value, valueColor }) => (
  <div className="flex flex-col items-center text-center p-2">
    <p
      className="text-base sm:text-lg md:text-lg font-semibold break-all font-roboto"
      style={{ color: valueColor || "#454545" }}
    >
      {value ?? "-"}
    </p>
    <p className="text-xs sm:text-sm mt-1 text-[#7F7F7F] font-roboto">
      {label}
    </p>
  </div>
);

export default ProfileSummary;
